import { createSelector } from 'reselect';
import { redux } from 'Core/redux';
import { STORE_KEY, INITIAL_STATE } from './slice';

export const selectState = state => {
  const appState = state || redux.store.getState();
  return appState?.[STORE_KEY] || INITIAL_STATE;
};

export const selectPlannerData = state => selectState(state).data || INITIAL_STATE.data;

export const selectElements = state => selectState(state).data?.elements || INITIAL_STATE.data.elements;
export const selectPrograms = state => selectState(state).data?.programs || INITIAL_STATE.data.programs;
export const selectChannels = state => selectState(state).data?.channels || INITIAL_STATE.data.channels;
export const isPlannerLoading = state => selectState(state).loading;
export const selectPlanFilters = state => selectState(state).filters;

/**
 * Collect searchable strings for keyword filter (work ticket, part, description).
 * Splits on " - " so bar labels like "450637 - 32-839557" match part or WT alone.
 */
export function getPlannerKeywordHaystacks(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const haystacks = [];
  const push = v => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s) return;
    haystacks.push(s);
    if (s.includes(' - ')) {
      s.split(/\s-\s/).forEach(seg => {
        const t = seg.trim();
        if (t) haystacks.push(t);
      });
    }
  };

  [
    entry.workTicketNo,
    entry.workTicketNumber,
    entry.workTicket,
    entry.workorderid,
    entry.workOrderId,
    entry.processIdExt,
    entry.wtname,
    entry.title,
  ].forEach(push);

  [
    entry.partNumber,
    entry.PartNumber,
    entry.part_PartNumber,
    entry.partNo,
    entry.part_code,
    entry.partCode,
    entry.standarD_ID,
    entry.itemNumber,
    entry.itemCode,
    entry.description,
    entry.Description,
  ].forEach(push);

  if (Array.isArray(entry.processParts)) {
    entry.processParts.forEach(p => {
      push(p?.partNumber);
      push(p?.PartNumber);
      push(p?.partNo);
      push(p?.part_code);
      push(p?.partCode);
      push(p?.description);
      push(p?.Description);
    });
  }

  return haystacks;
}

export function plannerEntryMatchesKeyword(entry, searchText) {
  const q = (searchText || '').toLowerCase().trim();
  if (!q) return true;
  return getPlannerKeywordHaystacks(entry).some(h => h.toLowerCase().includes(q));
}

export const selectFilteredPlans = createSelector(
  [selectPrograms, selectPlanFilters],
  (data, { searchText, status, start, end }) => {
    // Ensure data is an array before filtering
    if (!Array.isArray(data)) {
      return [];
    }
    const keywordActive = (searchText || '').trim() !== '';

    return data.filter(entry => {
      const matchesSearchText = plannerEntryMatchesKeyword(entry, searchText);

      // Keyword search ignores From/To, Year/Month/Day range, and Status (marked filter area).
      if (keywordActive) {
        return matchesSearchText;
      }

      // status filter (form / Redux filters)
      const matchesStatus = status && status !== 'All' ? entry.status === status : true;

      // date filter - check for overlap between program dates and filter dates
      const matchesDate = (() => {
        if (!start || !end) return true;

        const programStart = new Date(entry.since);
        if (Number.isNaN(programStart.getTime())) return false;

        let programEnd;
        const tillRaw = entry.till;
        if (tillRaw != null && String(tillRaw).trim() !== '') {
          programEnd = new Date(tillRaw);
        }
        if (programEnd == null || Number.isNaN(programEnd.getTime())) {
          programEnd = new Date(programStart);
          programEnd.setHours(23, 59, 59, 999);
        }

        const filterStart = new Date(start + 'T00:00:00');
        const filterEnd = new Date(end + 'T23:59:59');

        // Check for overlap: program starts before filter ends AND program ends after filter starts
        return programStart <= filterEnd && programEnd >= filterStart;
      })();

      return matchesSearchText && matchesStatus && matchesDate;
    });
  },
);

export const selectStatusLog = state => selectState(state).statusLog;
export const selectStatusLogLoading = state => selectState(state).statusLogLoading;
export const selectStatusLogError = state => selectState(state).statusLogError;

// Memoized selector to avoid creating new object references
export const selectCompletionStatus = createSelector(
  [selectState],
  (plannerState) => plannerState?.completionStatus || {}
);

export const selectCompletionStatusLoading = state => selectState(state).completionStatusLoading;